import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pb } from '@/core/api';
import { useAuthStore } from '@/stores/auth';

type ApiError = { data?: { error?: string } };
const MIN_LEN = 8;

export type ChangePasswordState = {
  current: string;
  setCurrent: (v: string) => void;
  next: string;
  setNext: (v: string) => void;
  confirm: string;
  setConfirm: (v: string) => void;
  error: string;
  loading: boolean;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
};

function validate(next: string, confirm: string): string {
  if (next !== confirm) return 'Las contraseñas no coinciden';
  if (next.length < MIN_LEN) return `Mínimo ${MIN_LEN} caracteres`;
  return '';
}

async function submit(body: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  await pb.send('/api/team/me/change-password', { method: 'POST', body });
}

type SubmitArgs = {
  body: { current_password: string; new_password: string };
  setS: (s: { error: string; loading: boolean }) => void;
  refreshAuth: () => Promise<unknown>;
  nav: (to: string, opts: { replace: boolean }) => void;
};

function runSubmit(a: SubmitArgs): void {
  a.setS({ error: '', loading: true });
  submit(a.body)
    .then(() => a.refreshAuth())
    .then(() => a.nav('/', { replace: true }))
    .catch((err: ApiError) =>
      a.setS({
        error: err?.data?.error ?? 'Error al cambiar contraseña',
        loading: false,
      })
    );
}

type Fields = {
  current: string;
  setCurrent: (v: string) => void;
  next: string;
  setNext: (v: string) => void;
  confirm: string;
  setConfirm: (v: string) => void;
};

function useFields(): Fields {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  return { current, setCurrent, next, setNext, confirm, setConfirm };
}

export function useChangePassword(): ChangePasswordState {
  const f = useFields();
  const [s, setS] = useState({ error: '', loading: false });
  const refreshAuth = useAuthStore((x) => x.refreshAuth);
  const nav = useNavigate();
  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const err = validate(f.next, f.confirm);
    if (err) return setS({ error: err, loading: false });
    runSubmit({
      body: { current_password: f.current, new_password: f.next },
      setS,
      refreshAuth,
      nav,
    });
  }
  return { ...f, error: s.error, loading: s.loading, handleSubmit };
}
